import * as React from "react";
import { useEffect, useRef, useState } from "react";
import { inputService } from "@modules/inputs/input/InputService";
import { useTranslation } from "react-i18next";
import "./DebugOverlay.component.css";
import { StatsTab } from "./stats-tab/StatsTab.component";
import { MessageTab } from "./message-tab/MessageTab.component";
import { PerformanceTab } from "./performance-tab/PerformanceTab.component";
import { ModelTab } from "./model-tab/ModelTab.component";
import { ScriptTab } from "./script-tab/ScriptTab.component";
import { CollisionTab } from "./collision-tab/CollisionTab.component";
import { TrainingTab } from "./training-tab/TrainingTab.component";
const tabs = ["stats", "models", "messages", "scripts", "collision", "performance", "training"];
const MIN_DIALOG_WIDTH = 800;
const MIN_DIALOG_HEIGHT = 600;
export const DebugOverlay = () => {
    const [visible, setVisible] = useState(false);
    const [activeTab, setActiveTab] = useState("stats");
    const { t } = useTranslation();
    // Make dialog scalable
    const [size, setSize] = useState({
        width: Math.max(1000, window.innerWidth * 0.4),
        height: window.innerHeight * 0.7
    });
    const [resizing, setResizing] = useState(null);
    const resizeStart = useRef({ mouseX: 0, mouseY: 0, x: 0, y: 0, width: 0, height: 0 });
    // Make dialog movable
    const [position, setPosition] = useState(() => ({
        // These have to match the definition of min-size and min-height in the css
        x: window.innerWidth / 2 - Math.max((Math.max(1000, window.innerWidth * 0.4)) / 2, 800 / 2),
        y: window.innerHeight / 2 - Math.max((window.innerHeight * 0.7) / 2, 600 / 2)
    }));
    const [dragging, setDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });
    useEffect(() => {
        inputService
            .registerAction("ToggleDebugDialog", () => {
            setVisible((v) => !v);
        }, t("common:key-description-key-o", "Toggle debug dialog"))
            .addKey("KeyO")
            .asDev();
    }, []);
    useEffect(() => {
        if (!visible)
            return;
        const handleKeyDown = (e) => {
            if (e.key !== "Tab")
                return;
            e.preventDefault(); // prevent normal focus navigation
            setActiveTab((current) => {
                const currentIndex = tabs.indexOf(current);
                if (e.shiftKey) {
                    // backwards
                    return tabs[(currentIndex - 1 + tabs.length) % tabs.length];
                }
                else {
                    // forward
                    return tabs[(currentIndex + 1) % tabs.length];
                }
            });
        };
        window.addEventListener("keydown", handleKeyDown);
        return () => {
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [visible]);
    // Scaling effect
    useEffect(() => {
        if (resizing == null)
            return;
        const onMouseMove = (e) => {
            const dx = e.clientX - resizeStart.current.mouseX;
            const dy = e.clientY - resizeStart.current.mouseY;
            const rightEdge = resizeStart.current.x + resizeStart.current.width;
            const bottomEdge = resizeStart.current.y + resizeStart.current.height;
            let width = resizeStart.current.width;
            let height = resizeStart.current.height;
            let x = resizeStart.current.x;
            let y = resizeStart.current.y;
            if (resizing.includes("right")) {
                width = Math.max(MIN_DIALOG_WIDTH, resizeStart.current.width + dx);
            }
            if (resizing.includes("left")) {
                width = Math.max(MIN_DIALOG_WIDTH, resizeStart.current.width - dx);
                x = rightEdge - width;
            }
            if (resizing.includes("bottom")) {
                height = Math.max(MIN_DIALOG_HEIGHT, resizeStart.current.height + dy);
            }
            if (resizing.includes("top")) {
                height = Math.max(MIN_DIALOG_HEIGHT, resizeStart.current.height - dy);
                y = bottomEdge - height;
            }
            setSize({ width, height });
            setPosition({ x, y });
        };
        const onMouseUp = () => {
            setResizing(null);
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [resizing]);
    // Dragging effect
    useEffect(() => {
        if (!dragging)
            return;
        const onMouseMove = (e) => {
            setPosition({
                x: e.clientX - dragStart.current.x,
                y: e.clientY - dragStart.current.y
            });
        };
        const onMouseUp = () => {
            setDragging(false);
        };
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        return () => {
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
        };
    }, [dragging]);
    if (!visible)
        return null;
    const startResize = (direction) => (e) => {
        e.stopPropagation();
        setResizing(direction);
        resizeStart.current = {
            mouseX: e.clientX,
            mouseY: e.clientY,
            x: position.x,
            y: position.y,
            width: size.width,
            height: size.height
        };
    };
    return (React.createElement("div", { className: "debug-dialog-backdrop" },
        React.createElement("div", { className: "debug-dialog", style: {
                left: position.x,
                top: position.y,
                width: size.width,
                height: size.height,
                border: "5px solid var(--info-color)",
                borderRadius: "16px"
            }, onClick: (e) => {
                e.stopPropagation();
            } },
            React.createElement("header", { className: "debug-dialog-header", onMouseDown: (e) => {
                    setDragging(true);
                    dragStart.current = {
                        x: e.clientX - position.x,
                        y: e.clientY - position.y
                    };
                } },
                React.createElement("button", { className: activeTab === "stats" ? "active" : "", onClick: () => {
                        setActiveTab("stats");
                    } }, "Stats"),
                React.createElement("button", { className: activeTab === "models" ? "active" : "", onClick: () => {
                        setActiveTab("models");
                    } }, "Models"),
                React.createElement("button", { className: activeTab === "messages" ? "active" : "", onClick: () => {
                        setActiveTab("messages");
                    } }, "Messages"),
                React.createElement("button", { className: activeTab === "scripts" ? "active" : "", onClick: () => {
                        setActiveTab("scripts");
                    } }, "Scripts"),
                React.createElement("button", { className: activeTab === "collision" ? "active" : "", onClick: () => {
                        setActiveTab("collision");
                    } }, "Collision"),
                React.createElement("button", { className: activeTab === "performance" ? "active" : "", onClick: () => {
                        setActiveTab("performance");
                    } }, "Performance"),
                React.createElement("button", { className: activeTab === "training" ? "active" : "", onClick: () => {
                        setActiveTab("training");
                    } }, "Training")),
            React.createElement("section", { className: "debug-dialog-content", style: {
                    border: "1px solid var(--border-color)",
                    borderLeft: "0",
                    borderRight: "0",
                    borderBottom: "0"
                } },
                activeTab === "stats" && React.createElement(StatsTab, null),
                activeTab === "models" && React.createElement(ModelTab, null),
                activeTab === "messages" && React.createElement(MessageTab, null),
                activeTab === "scripts" && React.createElement(ScriptTab, null),
                activeTab === "collision" && React.createElement(CollisionTab, null),
                activeTab === "performance" && React.createElement(PerformanceTab, null),
                activeTab === "training" && React.createElement(TrainingTab, null)),
            React.createElement("div", { className: "resize-handle resize-handle-top", onMouseDown: startResize("top") }),
            React.createElement("div", { className: "resize-handle resize-handle-right", onMouseDown: startResize("right") }),
            React.createElement("div", { className: "resize-handle resize-handle-bottom", onMouseDown: startResize("bottom") }),
            React.createElement("div", { className: "resize-handle resize-handle-left", onMouseDown: startResize("left") }),
            React.createElement("div", { className: "resize-handle resize-handle-top-left", onMouseDown: startResize("top-left") }),
            React.createElement("div", { className: "resize-handle resize-handle-top-right", onMouseDown: startResize("top-right") }),
            React.createElement("div", { className: "resize-handle resize-handle-bottom-left", onMouseDown: startResize("bottom-left") }),
            React.createElement("div", { className: "resize-handle resize-handle-bottom-right", onMouseDown: startResize("bottom-right") }))));
};
